import {uuid} from 'uuidv4';
import {round} from "utils/round.js";
import {zmessageUpdated} from './subpatch/operations.js';
import {setPresentationWindowPosition} from 'actions/index.js';
import {safeStringify} from 'utils/isCyclic.js';
import {isNumber} from "utils/IsNumber.js";
import {ZMESSAGE} from 'constants/object-types.js';
import NodeMessaging from "messaging/NodeMessaging.js";
import {BANG, LIST, SYMBOL, NUMBER} from './data-types.js';
import HasAttributes from "zobject/HasAttributes.js";
import Dispatchable from "utils/Dispatchable.js";

export default class ZMessage extends HasAttributes(Dispatchable(NodeMessaging(class {}))) {
    constructor(subSequence, type=SYMBOL, onInput, id) {
        super();
        this.subSequence = subSequence;
        this.sequencerEngine = subSequence.sequencerEngine;
        this.text = "";
        this.type = type;
        this.window = {
            id: uuid()
        };

        if (type === BANG) {
            this.message = BANG;
            this.storedMesage = BANG;
        } else if (type === NUMBER) {
            this.text = 0;
        }
        if (id) {
            this.id = id;
        } else {
            this.id = uuid();
        }
        this.sequencerEngine.idToObject[this.id] = this;
        if (onInput) {
            this.newInlet("Input", (x) => {
                if (this._onExecute) {
                    this._onExecute(x[0]);
                } else {
                    onInput(x[0], ...x.splice(1));
                }
            });
        } else {
            this.newInlet("Input", (x) => {
                if (this.type === NUMBER && !isNaN(x[0])) {
                    this._text = x[0];
                    this.storedMessage = x[0];
                    this.execute(this.storedMessage);
                } else if (x[0] === "bang") {
                    this.execute();
                } else {
                    this.storedMessage = this.pipeText(x[0]);
                    this.execute(this.storedMessage);
                }
            });
        }
        if (type !== BANG ) {
            this.newInlet("Replace", (x) => this.onReplaceText(x));
        }
        this.newOutlet("Output");
        if (type !== NUMBER) {
            this.newOutlet("Message Send");
        }

        this.min = 0;
        this.max = 100;
        this.round = false;

        this.newAttribute("primary-color");
        this.newAttribute("secondary-color");
        this.newAttribute("background-color");
        this.newAttribute("width", 0);
        this.newAttribute("align to center", false);
        this.newAttribute("Include in Presentation", false);
    }

    getObjectType() {
        return ZMESSAGE;
    }

    getAllParameters() {
        return [];
    }

    getJson() {
        return {
            id: this.id,
            attributes: this.attributes, 
            isDeleted: this.isDeleted,
            type: this.type,
            text: this._text,
            message: this.type === NUMBER ? this.message : undefined,
            inletNumber: this.inletNumber,
            outletNumber: this.outletNumber,
            min: this.min,
            max: this.max,
            round: this.round
        };
    }

    fromJson(json, isCopy) {
        if (!isCopy) {
            this.id = json.id;
            this.sequencerEngine.idToObject[this.id] = this;
        }
        if (json.type !== undefined) {
            this.type = json.type;
        }


        this.isDeleted = json.isDeleted;

        if (json.type === NUMBER) {
            this.text = json.text;
        } else {
            this.text = json.text;
        }
        if (json.type !== undefined) {
            this.type = json.type;
            if (this.type === BANG) {
                this.message = BANG;
                this.storedMessage = BANG;
            }
        }
        this.inletNumber = json.inletNumber;
        this.outletNumber = json.outletNumber;

        if (this.inletNumber !== undefined) {
            this.isInlet = true;
        }
        if (this.outletNumber !== undefined) {
            this.isOutlet = true;
        }

        if (json.min !== undefined) {
            this.min = json.min;
        }
        if (json.max !== undefined) {
            this.max = json.max;
        }
        if (json.round !== undefined) {
            this.round = json.round;
        }

        if (json.attributes) {
            for (let name in json.attributes) {
                this.setAttribute(name, json.attributes[name]);
            }
        }
    }

    sendReplacement(x) {
        x = x[0]
        if (typeof this.message === "string") {
            let msg = this.message.replaceAll("$1", x);
            this.execute(msg);
        } else {
            this.execute(x);
        }
    }

    pipeText(x) {
        if (this.type === LIST) {
            let y = Array.isArray(x) ? x : [x];
            return this.message.map(
                x => {
                    let text = x;
                    if (typeof x === "string") {
                        for (let i=0; i < y.length; i++ ){
                            text = text.replaceAll("$" + (i+1), y[i]);
                        }
                        if (isNumber(text)) {
                            text = parseFloat(text);
                        }
                    }
                    return text;
                });
        }
        if (typeof this.message !== "string") {
            return this.message;
        }

        let text = this.message;
        if (Array.isArray(x)) {
            for (let i=0; i < x.length; i++ ){
                text = text.replaceAll("$" + (i+1), round(x[i], 3));
            }
        } else {
            if (typeof x === "string") {
                text = text.replaceAll("$1", x);
            } else {
                text = text.replaceAll("$1", round(x, 3));
            }
        }
        return text;
    }


    onReplaceText(x) {
        if (x[0] === null) {
            return;
        }
        this.message = x[0];
        this.lastMessage = this.message;
        if (Array.isArray(this.message)) {
            this._text = this.message.map(
                x => typeof x === "object" ? safeStringify(x) : x).join(" ");
        } else if (ArrayBuffer.isView(this.message)) {
            this._text = Array.from(this.message).join(" ");
        } else {
            if (Array.isArray(x[0])) {
                this._text = x[0].join(" ");
            } else if (typeof x[0] === "string" || typeof x[0] === "number") {
                this._text = x[0];
            } else if (x[0]) {
                if (x[0].getJson) {
                    this._text = safeStringify(x[0].getJson());
                } else {
                    try {
                        this._text = safeStringify(x[0]);
                    } catch (e) {
                        this._text = "input";
                    }
                }
            }
        }
        this.storedMessage = this.message;
    }

    set text(text) {
        this.subSequence.propagate(
            zmessageUpdated(this, text));

        if (this.type === NUMBER) {
            this._text = text;
            this.message = text;
            this.storedMessage = this.message;
            return;
        }
        if (!text) {
            text = '';
        }
        text = text.toString();

        if (this.type === NUMBER) {
            this._text = text;
            this.message = parseFloat(text);
            this.storedMessage = this.message;
            return;
        }

        this._text = text;

        let tokens = text.split(" ").filter(x => x != "");

        if (tokens.length > 1) {
            // then we have a list
            let list = [];
            this.type = LIST;
            try {
                // try to parse everything into numbers
                list = tokens.map(x => x.startsWith("$") ? x : parseFloat(x));
                if (list.some(x => (!x.startsWith || !x.startsWith("$")) && isNaN(x))) {
                    this.message = text;
                    this.storedMessage = this.message;
                    this.type = SYMBOL;
                    return;
                }
            } catch (e) {
                this.type = SYMBOL;
                this.storedMessage = this.message;
                this.message = text;
                return;
            }
            this.message = list;
            this.storedMessage = this.message;
        } else {
            // its either a string or number
            try {
                this.message = isNumber(text) ? parseFloat(text) : text;
                this.storedMessage = this.message;
                if (isNaN(this.message)) {
                    this.message = text;
                }
            } catch (e) {
                // its a string
                this.message = text;
                this.storedMessage = this.message;
            }
        }
    }

    refresh() {
        if (this.lastSentMessage) {
            this.execute(this.lastSentMessage);
        }
    }

    execute(msg=this.storedMessage) {
        // sends the message contained out the outlet
        this.lastSentMessage = msg;
        this.sendMessage("Output", msg);
        this.sendMessage("Message Send", BANG);
    }

    duplicate() {
        let json = this.getJson();
        let {x,y} = this.state.windows.windowPositions[this.id];
        let width = this.width || 200;
        let height = this.width || 30;
        let zmessage= this.subSequence.createZMessage(x+width,y, this.dataType);
        zmessage.fromJson(json, true);

        let presentationPosition = this.state.windows.presentationWindowPositions[this.id];
        if (presentationPosition) {
            this.dispatch(setPresentationWindowPosition(zmessage.id, {
                x: presentationPosition.x +width,
                y: presentationPosition.y
            }));
        }
    }

}


